import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Oekt } from "../state/session";
import * as oppsett from "../state/oppsett";
import {
  hentSerier, lastOpp, type Serie, type Slag,
} from "../state/dokumenter";
import { useLevende } from "../state/levende";
import { useGlidendeKort } from "../components/glidendeKort";

/**
 * Koordinatorens flate: alt som skal lastes opp, paa ett sted.
 *
 * Kortene har hurtigknappenes form — fylt flate, etiketten i bunnen, og en
 * smal fargestripe langs venstre kant. Stripa er ikke pynt: den er det som
 * gjoer at et rutenett kan leses uten aa lese teksten, og den er grunnen til
 * at koordinatoren ser hele dagen i ett blikk.
 *
 * Alle opplastingene er paa denne ene flata. En koordinator som skal laste opp
 * tre dokumenter skal ikke navigere tre steder.
 *
 * Prodleder ser den samme flata, pluss ett kort. Rollen gir ett element mer,
 * ikke en annen flate — da finnes det én ting aa vedlikeholde og én aa laere.
 */

interface Felt {
  readonly id: string;
  readonly slag: Slag;
  readonly tittel: string;
  readonly farge: string;
  readonly versjon: number | null;
  readonly filnavn: string | null;
  readonly lastetOpp: string | null;
  /** Klokkeslett versjonen blir synlig i appen. Tom betyr med én gang. */
  readonly visesFra: string;
  /** Raden klokkeslettet skal settes paa. Null naar ingenting er lastet opp. */
  readonly dokumentId: string | null;
  /** Satt mens fila gaar opp, saa kortet kan si det. */
  readonly laster: boolean;
  readonly feil: string | null;
}

/* Fargene foelger markoerpaletten, i samme rekkefoelge som hurtigknappene
   deles ut. Rekkefoelgen er stabil, saa dagsmanus er alltid gul. */
const PALETT = [
  "var(--marker-gul)", "var(--marker-bla)", "var(--marker-gronn)",
  "var(--marker-lilla)", "var(--marker-rust)", "var(--marker-gra)",
] as const;

/*
 * De faste feltene finnes alltid, ogsaa foer noen har lastet opp noe.
 *
 * En koordinator som aapner flata skal se hvor dagsplanen skal — ikke en tom
 * side med en pluss-knapp. Seriene i databasen opprettes foerst ved foerste
 * opplasting; disse er plassene de lander paa.
 */
const FASTE: readonly { slag: Slag; tittel: string }[] = [
  { slag: "dagsmanus", tittel: "Dagsmanus" },
  /* Redaktoerens gjennomgang av hva hver scene krever. Lastes opp som de
     andre planene — se den ufravikelige regelen om PDF-er i CLAUDE.md. */
  { slag: "nedbrekk", tittel: "Nedbrekk" },
  { slag: "dagsplan", tittel: "Dagsplan" },
  { slag: "kjoreplan", tittel: "Kjøreplan" },
  /* Plassen kameraplanen lander paa. Fram til eksporten finnes (fase 5) er
     det boksen A-foto laster opp sin egen fil i. */
  { slag: "kameraplan", tittel: "Kameraplan" },
];

/** ISO-tidspunkt → «i dag 06:40» eller «5. okt 06:40». */
function naar(iso: string): string {
  const d = new Date(iso);
  const klokke = d.toLocaleTimeString("no-NO", { hour: "2-digit", minute: "2-digit" });
  const iDag = new Date().toDateString() === d.toDateString();
  return iDag ? `i dag ${klokke}` : `${d.toLocaleDateString("no-NO", { day: "numeric", month: "short" })} ${klokke}`;
}

/** ISO-tidspunkt → «06:40» til tidsfeltet. Null blir tom streng. */
function klokkeslett(iso: string | null): string {
  if (iso === null) return "";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Fletter det som ligger i databasen med de faste plassene.
 *
 * Faste felter foerst, i fast rekkefoelge — dagsmanus er alltid gul. Deretter
 * det prodleder har lagt til, i den rekkefoelgen de ble opprettet.
 */
function flett(serier: readonly Serie[]): Felt[] {
  /*
   * ETT FELT HETER ÉN TING.
   *
   * Et felt prodleder lagde selv kan hete det samme som et fast felt — enten
   * fordi det faste kom til etterpaa, eller fordi hen skrev det samme navnet.
   * Da skal de vaere SAMME boks. To bokser som heter «Kameraplan», der bare
   * den ene har fila, er ikke til aa forstaa.
   */
  const samme = (a: string, b: string): boolean =>
    a.trim().toLocaleLowerCase("nb") === b.trim().toLocaleLowerCase("nb");

  const faste = FASTE.map((f, i): Felt => {
    const s = serier.find((x) => x.slag === f.slag)
      ?? serier.find((x) => x.slag === "annet" && samme(x.tittel, f.tittel));
    return {
      id: s?.id ?? f.slag,
      slag: f.slag,
      tittel: f.tittel,
      farge: PALETT[i % PALETT.length] ?? PALETT[0],
      versjon: s?.siste?.versjon ?? null,
      filnavn: s?.siste?.filnavn ?? null,
      lastetOpp: s?.siste === undefined || s.siste === null ? null : naar(s.siste.lastetOpp),
      visesFra: klokkeslett(s?.siste?.visesFra ?? null),
      dokumentId: s?.siste?.id ?? null,
      laster: false,
      feil: null,
    };
  });

  const egne = serier
    .filter((s) => s.slag === "annet"
      && !FASTE.some((f) => samme(f.tittel, s.tittel)))
    .map((s, i): Felt => ({
      id: s.id,
      slag: "annet",
      tittel: s.tittel,
      farge: PALETT[(FASTE.length + i) % PALETT.length] ?? PALETT[0],
      versjon: s.siste?.versjon ?? null,
      filnavn: s.siste?.filnavn ?? null,
      lastetOpp: s.siste === null ? null : naar(s.siste.lastetOpp),
      visesFra: klokkeslett(s.siste?.visesFra ?? null),
      dokumentId: s.siste?.id ?? null,
      laster: false,
      feil: null,
    }));

  return [...faste, ...egne];
}

export function OpplastingScreen(
  { oekt, bokser }: {
    readonly oekt: Oekt;
    /* Boksene omraadet viser. Tom liste betyr alle — se state/omraade.ts. */
    readonly bokser: readonly Slag[];
  },
): JSX.Element {
  const [alle, setFelter] = useState<readonly Felt[]>(() => flett([]));
  const [henter, setHenter] = useState(true);
  /* Boksen som spoer naar opptaksdagen gaar. Kommer etter en dagsplan. */
  const [spoerOmDagen, setSpoerOmDagen] = useState(false);
  /* Naar dokumentene for DENNE dagen tar over i appen. Foelger av forrige
     dags sluttidspunkt — se oppsett.visesFra. */
  const [visesFra, setVisesFra] = useState<Date | null>(null);
  /* Nye bokser glir inn, og naboene glir med. Se `glidendeKort.ts`. */
  const settKort = useGlidendeKort();
  const mineRoller = [oekt.member.role, ...(oekt.member.extraRoles ?? [])];
  const erProdleder = mineRoller.includes("prodleder");
  /*
   * HVEM SETTER OPPTAKSDAGENS VINDU.
   *
   * Prodleder, produsent og koordinator — de samme som basen slipper
   * gjennom, se `tilgang.setter_dagene` i migrasjon 20261102140000.
   * Knappen sto hos alle, saa regi fikk et valg basen ville nektet. En
   * knapp som alltid feiler er verre enn ingen knapp.
   */
  const setterDagene = mineRoller.some(
    (r) => r === "prodleder" || r === "produsent" || r === "koordinator");

  /*
   * Omraadet rydder bort bokser, det LUKKER dem ikke.
   *
   * Et egendefinert felt staar i alle omraader: det er prodleder som har
   * laget det, og da vet ingen her hvem det er til.
   */
  const felter = bokser.length === 0
    ? alle
    : alle.filter((f) => f.slag === null || f.slag === "annet" || bokser.includes(f.slag));

  const lesInn = useCallback(async (): Promise<void> => {
    setFelter(flett(await hentSerier(oekt)));
    setHenter(false);
  }, [oekt]);

  useEffect(() => { void lesInn(); }, [lesInn]);

  /* Laster koordinatoren opp en ny versjon mens prodleder ser på, kommer
     den fram uten at noen må laste sida om. */
  useLevende("opplasting", ["document", "document_series"], () => { void lesInn(); });

  /*
   * Egne felter opprettes foerst naar det lastes opp i dem.
   *
   * `document_series` krever et slag og en tittel, men en tom serie uten fil
   * er ingenting verdt — og den ville ligget igjen hvis koordinatoren ombestemte
   * seg. Kortet finnes derfor lokalt til foerste opplasting; da blir serien
   * til, og neste innlesing henter den fra databasen.
   */
  const leggTil = useCallback((): void => {
    const tittel = window.prompt("Hva heter feltet?")?.trim();
    if (tittel === undefined || tittel.length === 0) return;
    setFelter((f) => [
      ...f,
      {
        id: `ny-${Date.now()}`,
        slag: "annet",
        tittel,
        farge: PALETT[f.length % PALETT.length] ?? PALETT[0],
        versjon: null, filnavn: null, lastetOpp: null, visesFra: "",
        dokumentId: null, laster: false, feil: null,
      },
    ]);
  }, []);

  const lesVisesFra = useCallback((): void => {
    void (async () => {
      setVisesFra(await oppsett.visesFra(oekt.production.id, oekt.shootDay));
    })();
  }, [oekt.production.id, oekt.shootDay]);
  useEffect(lesVisesFra, [lesVisesFra]);

  const velgFil = useCallback(async (id: string, fil: File): Promise<void> => {
    const felt = felter.find((x) => x.id === id);
    if (felt === undefined) return;

    setFelter((f) => f.map((x) => (x.id === id ? { ...x, laster: true, feil: null } : x)));

    /* Navnet er `gammeltKlokkeslett` og ikke `visesFra`: feltet i flata
       er borte, og dette er bare den gamle kolonnen som fortsatt tas
       imot av `lastOpp`. Uten omdoepingen skygget den for tilstanden
       med samme navn. */
    const gammeltKlokkeslett = felt.visesFra === "" ? null : (() => {
      const [t, m] = felt.visesFra.split(":").map(Number);
      const d = new Date();
      d.setHours(t ?? 0, m ?? 0, 0, 0);
      return d.toISOString();
    })();

    const svar = await lastOpp(
      oekt, felt.slag, felt.tittel, fil, gammeltKlokkeslett);
    if (!svar.ok) {
      setFelter((f) => f.map((x) => (x.id === id
        ? { ...x, laster: false, feil: svar.feil ?? "Ukjent feil." } : x)));
      return;
    }
    // Les inn paa nytt framfor aa gjette hva som ble lagret. Versjonsnummeret
    // avgjoeres av databasen, ikke av oss.
    await lesInn();

    /*
     * DAGSPLANEN ER DER DAGEN BLIR BESTEMT.
     *
     * Brukeren 02.11.2026: «Start-stop paa opptaksdagen kommer til aa
     * bli definert av koordinator/prodleder/produsent naar de laster
     * opp dagsplanen.» Det er riktig sted: planen ER tidene, og den som
     * laster den opp har dem foran seg.
     *
     * Boksen kommer ETTER opplastingen, ikke foer. Filen er levert
     * uansett hva som skjer i boksen — en plan som ikke kom fram fordi
     * noen lukket et vindu ville vaert en darlig bytte.
     */
    if (felt.slag === "dagsplan" && setterDagene) setSpoerOmDagen(true);
  }, [felter, oekt, lesInn, setterDagene]);

  /*
   * FJERNINGEN LIGGER I KONSOLLEN, IKKE HER.
   *
   * Brukeren 30.10.2026: «I selve arbeidsflaten skal man ikke kunne
   * slette dokumenter osv, men i konsollen skal man ha kontroll på alt.»
   * Det er en riktig deling. Dette er flata man jobber på en opptaksdag,
   * og ingen skal kunne rive bort grunnlaget mens noen står på sett —
   * heller ikke ved et uhell, og `Fjern` sto i hjørnet av en flate som er
   * slippsone i hele sin bredde.
   *
   * Policyen `slett_dokumenter` står; det er veien hit som er stengt.
   */

  return (
    <div className="flate">
      <div className="flate__innhold">
        <div className="rutenett">
          {felter.map((f) => (
            <Kort
              key={f.id}
              felt={f}
              settKort={settKort}
              onFil={(fil) => { void velgFil(f.id, fil); }}
              visesFra={visesFra}
              /*
                * Dagsplanen baerer naar dagen GAAR. Boksen kommer av seg
                * selv etter en opplasting, men den maa ogsaa kunne
                * aapnes etterpaa: tidene rettes oftere enn planen lastes
                * opp paa nytt.
                *
                * IKKE FOER DET ER LASTET OPP NOE. Brukeren 03.11.2026:
                * «Valget naar gaar opptaksdagen skal ikke komme opp foer
                * man har lastet opp en dagsplan.» Planen ER tidene — en
                * knapp som spoer naar dagen gaar paa et kort uten plan
                * spoer om noe ingen har svaret paa ennaa.
                */
              onDagsvindu={f.slag === "dagsplan" && setterDagene
                && f.dokumentId !== null
                ? () => setSpoerOmDagen(true) : undefined}
            />
          ))}

          {erProdleder ? (
            /* Ogsaa denne glir. Den staar utenfor lista, men den flytter
               seg like mye som kortene naar det kommer bokser foran. */
            <button
              type="button"
              ref={(el) => settKort("legg-til", el)}
              className="kort kort--ny"
              onClick={leggTil}
            >
              <span className="kort--ny__pluss" aria-hidden="true">+</span>
              <span className="kort__tittel">Legg til felt</span>
              <span className="kort__status">Blir en ny side i appen</span>
            </button>
          ) : null}
        </div>

        <p className="merknad">
          {henter
            ? "Henter dokumenter …"
            : "Dokumentene lagres i Supabase og dukker opp i appen. "
              + "En ny fil blir en ny versjon; den gamle blir stående."}
        </p>
      </div>

      {/*
        * PORTAL, UT AV PAGEREN.
        *
        * Flatene ligger i en vannrett pager der hver side er `position:
        * relative` og pageren selv klipper. En boks som skal dekke hele
        * vinduet har ingenting der aa gjoere — og brukeren 02.11.2026
        * saa den ikke i det hele tatt. Her er den i `body`, som alle
        * andre vinduer paa flata.
        */}
      {!spoerOmDagen ? null : createPortal(
        <Dagsvindu
          oekt={oekt}
          paaFerdig={() => { setSpoerOmDagen(false); lesVisesFra(); }}
        />,
        document.body,
      )}
    </div>
  );
}

/**
 * «Når går opptaksdag 2?»
 *
 * ========  HVORFOR DEN KOMMER MED DAGSPLANEN  ========
 *
 * Brukeren 02.11.2026: «Start-stop på opptaksdagen kommer til å bli
 * definert av koordinator/prodleder/produsent når de laster opp
 * dagsplanen i nettflata.» Planen ER tidene — den som laster den opp har
 * dem foran seg, og det er det eneste øyeblikket noen vet dem sikkert.
 *
 * ========  DATO OG KLOKKESLETT PÅ BEGGE  ========
 *
 * Ikke bare klokkeslett. Et nattskift som begynner 18:00 og slutter
 * 03:30 slutter dagen ETTER, og en boks med bare klokkeslett ville
 * tvunget fram en gjetning om hvilken dato 03:30 hører til. Begge
 * feltene er `datetime-local`, og sluttdatoen foreslås som startdatoen —
 * de fleste dager går ikke over midnatt, og de som gjør det retter ett
 * tall.
 *
 * Filen er alt levert når denne kommer opp. «Hopp over» er derfor et
 * ekte valg og ikke en halv opplasting: dagen beholder datoen sin, som
 * før.
 */
function Dagsvindu({ oekt, paaFerdig }: {
  readonly oekt: Oekt;
  paaFerdig(): void;
}): JSX.Element {
  const [start, setStart] = useState("");
  const [slutt, setSlutt] = useState("");
  const [dato, setDato] = useState<string | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const [jobber, setJobber] = useState(false);

  useEffect(() => {
    void (async () => {
      const v = await oppsett.hentDagsvindu(oekt.production.id, oekt.shootDay);
      if (v === null) return;
      setDato(v.dato);
      /* Staar det noe fra foer, rettes det — det er en ny plan for den
         samme dagen. Ellers foreslaas dagens dato og et vanlig skift. */
      setStart(v.start !== null ? lokal(v.start) : `${v.dato}T08:00`);
      setSlutt(v.slutt !== null ? lokal(v.slutt) : `${v.dato}T18:00`);
    })();
  }, [oekt.production.id, oekt.shootDay]);

  const lagre = useCallback((): void => {
    setJobber(true);
    setFeil(null);
    void (async () => {
      const svar = await oppsett.settDagsvindu(
        oekt.production.id, oekt.shootDay,
        start === "" ? null : new Date(start).toISOString(),
        slutt === "" ? null : new Date(slutt).toISOString(),
      );
      setJobber(false);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke lagret."); return; }
      paaFerdig();
    })();
  }, [oekt.production.id, oekt.shootDay, start, slutt, paaFerdig]);

  const overMidnatt = start !== "" && slutt !== "" && slutt.slice(0, 10) !== start.slice(0, 10);

  return (
    <div className="vindu" role="dialog" aria-modal="true" aria-label="Opptaksdagen">
      <div className="vindu__teppe" aria-hidden="true" />
      <div className="vindu__boks">
        <header className="vindu__topp">
          <span className="vindu__tittel">Opptaksdag {oekt.shootDay}</span>
          <span className="vindu__steg">{dato ?? ""}</span>
          <button type="button" className="innlogging__tilbake" onClick={paaFerdig}>
            Hopp over
          </button>
        </header>

        <div className="vindu__spor">
          <div className="vindu__side">
            <p className="merknad">
              Dagsplanen er lastet opp. Når går dagen? Appen bruker dette til
              å vite hvilken opptaksdag den skal logge på — også etter
              midnatt.
            </p>

            <span className="oppstart__merke">Start</span>
            <input
              className="konsoll__felt" type="datetime-local" value={start}
              onChange={(e) => setStart(e.target.value)}
              aria-label="Når opptaksdagen begynner"
            />

            <span className="oppstart__merke">Slutt</span>
            <input
              className="konsoll__felt" type="datetime-local" value={slutt}
              onChange={(e) => setSlutt(e.target.value)}
              aria-label="Når opptaksdagen slutter"
            />

            {!overMidnatt ? null : (
              <span className="merknad">
                Dagen går over midnatt. Det er én opptaksdag, og loggen etter
                midnatt hører til den.
              </span>
            )}
            {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
          </div>
        </div>

        <footer className="vindu__bunn">
          <span />
          <button
            type="button"
            className="synk__knapp synk__knapp--hoved"
            disabled={jobber}
            onClick={lagre}
          >
            {jobber ? "Lagrer …" : "Lagre"}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * «04:00» naar det er i dag eller i morgen tidlig, ellers «3. nov 04:00».
 *
 * Klokkeslettet alene holder for det som skjer i naermeste doegn — det er
 * det man staar og venter paa — men en dagsplan kan lastes opp en uke i
 * forveien, og da sier et klokkeslett uten dato ingenting.
 */
function naarTekst(n: Date): string {
  const p = (x: number): string => String(x).padStart(2, "0");
  const klokke = `${p(n.getHours())}:${p(n.getMinutes())}`;
  const naa = new Date();
  const doegn = Math.round(
    (new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime()
      - new Date(naa.getFullYear(), naa.getMonth(), naa.getDate()).getTime())
    / 86_400_000);
  if (doegn === 0) return klokke;
  if (doegn === 1) return `i morgen ${klokke}`;
  return `${n.toLocaleDateString("nb-NO", { day: "numeric", month: "short" })} ${klokke}`;
}

/** ISO fra basen til det `datetime-local` vil ha: lokal tid, uten sone. */
function lokal(iso: string): string {
  const d = new Date(iso);
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    + `T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function Kort({ felt, settKort, onFil, onDagsvindu, visesFra }: {
  readonly felt: Felt;
  settKort(id: string, el: HTMLElement | null): void;
  onFil(f: File): void;
  /** Bare paa dagsplanen. Aapner boksen for naar opptaksdagen gaar. */
  onDagsvindu?: (() => void) | undefined;
  /** Naar dokumentene for dagen tar over i appen. Null naar dagen er ukjent. */
  readonly visesFra: Date | null;
}): JSX.Element {
  const input = useRef<HTMLInputElement | null>(null);
  const [over, setOver] = useState(false);
  /* Hele kortflata er slippsone. Koordinatoren drar en PDF fra e-posten rett
     hit og skal ikke maatte treffe en liten knapp.

     Kortet er en `div` og ikke en knapp: klokkeslettfeltet ligger inni, og et
     skjemafelt inne i en knapp er hverken gyldig oppmerking eller noe man far
     til aa klikke paa. */
  const slipp = (e: React.DragEvent): void => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files[0];
    if (f !== undefined) onFil(f);
  };

  return (
    <div
      ref={(el) => settKort(felt.id, el)}
      className={`kort${over ? " kort--over" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={slipp}
    >
      <span className="kort__stripe" style={{ background: felt.farge }} aria-hidden="true" />

      <button type="button" className="kort__flate" onClick={() => input.current?.click()}>
        <span className="kort__tittel">{felt.tittel}</span>
        {felt.laster ? (
          <span className="kort__status">Laster opp …</span>
        ) : felt.feil !== null ? (
          /* Feilen staar paa kortet, ikke i en toast. Den hoerer til det ene
             dokumentet som ikke kom fram, og skal kunne leses i ro. */
          <span className="kort__status kort__status--feil">{felt.feil}</span>
        ) : felt.filnavn === null ? (
          <span className="kort__status">Ingen fil ennå</span>
        ) : (
          <>
            <span className="kort__fil mono">{felt.filnavn}</span>
            {/* «v2», ikke «Versjon 2». Brukeren 02.11.2026 ba om
                formen, og den er riktig: det er slik en filversjon
                skrives paa sett, og den staar ved siden av filnavnet
                der plassen er knapp. */}
            <span className="kort__status">v{felt.versjon} · {felt.lastetOpp}</span>
          </>
        )}
      </button>

      {onDagsvindu === undefined ? null : (
        <button
          type="button"
          className="synk__knapp kort__dagsvindu"
          onClick={onDagsvindu}
        >
          Når går opptaksdagen?
        </button>
      )}

      {/*
        * ========  «VISES FRA» ER IKKE LENGER ET VALG  ========
        *
        * Her sto et klokkeslettfelt: `document.visible_from`, satt for
        * haand, som SKJULTE dokumentet til tiden passerte. Det var feil
        * mekanisme — brukeren 02.11.2026 vil at planen skal kunne leses
        * med en gang, men ikke ta hovedplassen foer gaarsdagens
        * opptaksdag er over.
        *
        * Tidspunktet er dermed ikke noe man SETTER her, det er noe som
        * FOELGER av dagen: appen staar i en opptaksdag til den er over,
        * og neste dags dokumenter tar over naar forrige dag slutter. Et
        * felt som later som man kan overstyre det ville vaert en loegn —
        * appen leser ikke `visible_from` lenger i det hele tatt.
        *
        * Tidspunktet settes paa opptaksdagen, under «Naar gaar
        * opptaksdagen?» over. Se oppsett.visesFra.
        */}
      <span className="kort__tid">
        <span className="kort__tid-tekst">Vises fra</span>
        <span className="kort__tid-felt mono">
          {visesFra === null ? "—" : naarTekst(visesFra)}
        </span>
      </span>

      <input
        ref={input}
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f !== undefined) onFil(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
